import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle } from 'lucide-react';

const FAQSection = () => {
  const faqs = [
    {
      question: 'How does the pricing algorithm work?',
      answer: 'Our system analyzes 186+ live eBay listings in real-time, using 10 different pricing algorithms. We compare prices across eBay, Swappa, Back Market, Amazon, Apple Trade In, and GameStop to give you the most accurate market value. Platform fees are automatically calculated so you see true net values.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. All data is encrypted at rest and in transit. We use bank-level security standards. Your customer information, pricing data, and business analytics are never shared with third parties. You own your data completely.',
    },
    {
      question: 'Can I use this for my repair shop with multiple technicians?',
      answer: 'Yes! The Repair Hub feature supports multiple technicians with workload balancing and ticket assignment. Each technician can see their assigned repairs, and managers can track overall shop performance from the dashboard.',
    },
    {
      question: 'What devices are supported for pricing?',
      answer: 'We support all major electronics including iPhones, Samsung Galaxy, Google Pixel, iPads, MacBooks, PlayStation, Xbox, Nintendo Switch, and more. If it sells on eBay, we can price it accurately.',
    },
    {
      question: 'How accurate are the price estimates?',
      answer: 'Our pricing achieves 98% accuracy based on actual sold listings. We don\'t just look at asking prices - we analyze completed sales to show you what devices actually sell for in your condition.',
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes! Download the app for free from the App Store. You can explore all features with no commitment. Premium features are available with our subscription plans.',
    },
    {
      question: 'Do you offer email integration?',
      answer: 'Yes, we support both Gmail and Outlook through secure OAuth integration. Set up automated email notifications for repair status updates, pickup reminders, and review requests with beautiful HTML templates.',
    },
    {
      question: 'Can I customize the repair workflow?',
      answer: 'The repair workflow (Intake → Diagnosis → Repair → Complete) is designed for optimal efficiency, but you can customize notification triggers, email templates, and add custom fields to match your specific business needs.',
    },
  ];

  return (
    <section id="faq" className="py-16 sm:py-24 md:py-32 relative" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-12"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            Common
            <span className="text-gradient"> Questions</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400">
            Everything you need to know about 2A Valuence
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-4 sm:p-6 md:p-8"
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-b border-white/10 last:border-0"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-white hover:text-primary py-4 sm:py-5 text-sm sm:text-base md:text-lg font-medium">
                  <span className="flex items-start gap-2 sm:gap-3 pr-4">
                    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>{faq.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 pb-4 sm:pb-5 pl-6 sm:pl-8 text-sm sm:text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-6 sm:mt-8"
        >
          <p className="text-slate-400 text-sm sm:text-base">
            Still have questions?{' '}
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="text-primary hover:underline"
            >
              Contact us
            </button>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
